import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Menu, X, Calculator } from 'lucide-react';

const Header = () => {
  const { language, setLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
              isScrolled ? 'bg-gradient-to-br from-teal-500 to-teal-600' : 'bg-white/20 backdrop-blur-sm'
            }`}>
              <Calculator className="w-5 h-5 text-white" />
            </div>
            <span className={`font-semibold text-lg hidden sm:block transition-colors duration-300 ${
              isScrolled ? 'text-slate-800' : 'text-white'
            }`}>
              CF<span className={`${isScrolled ? 'text-teal-600' : 'text-teal-400'}`}>.Lda</span>
            </span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <button
              onClick={() => scrollToSection('services')}
              className={`text-sm font-medium transition-colors hover:text-teal-400 ${
                isScrolled ? 'text-slate-600' : 'text-white/90'
              }`}
            >
              {t.nav.services}
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className={`text-sm font-medium transition-colors hover:text-teal-400 ${
                isScrolled ? 'text-slate-600' : 'text-white/90'
              }`}
            >
              {t.nav.about}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className={`text-sm font-medium transition-colors hover:text-teal-400 ${
                isScrolled ? 'text-slate-600' : 'text-white/90'
              }`}
            >
              {t.nav.contact}
            </button>
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-4">
            {/* Language Switcher */}
            <div className={`flex items-center gap-1 rounded-full p-1 transition-colors ${
              isScrolled ? 'bg-slate-100' : 'bg-white/20 backdrop-blur-sm'
            }`}>
              <button
                onClick={() => setLanguage('pt')}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${
                  language === 'pt'
                    ? isScrolled 
                      ? 'bg-white text-teal-600 shadow-sm'
                      : 'bg-white text-teal-600 shadow-sm'
                    : isScrolled 
                      ? 'text-slate-500 hover:text-slate-700'
                      : 'text-white/80 hover:text-white'
                }`}
              >
                PT
              </button>
              <button
                onClick={() => setLanguage('en')}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${
                  language === 'en'
                    ? isScrolled 
                      ? 'bg-white text-teal-600 shadow-sm'
                      : 'bg-white text-teal-600 shadow-sm'
                    : isScrolled 
                      ? 'text-slate-500 hover:text-slate-700'
                      : 'text-white/80 hover:text-white'
                }`}
              >
                EN
              </button>
            </div>

            {/* CTA Button */}
            <Button
              onClick={() => scrollToSection('contact')}
              className="hidden sm:flex bg-teal-500 hover:bg-teal-600 text-white rounded-full px-5 py-2 text-sm font-medium transition-all hover:shadow-lg hover:shadow-teal-500/25"
            >
              {t.nav.getQuote}
            </Button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 transition-colors ${
                isScrolled ? 'text-slate-600 hover:text-teal-600' : 'text-white hover:text-teal-400'
              }`}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden absolute top-full left-0 right-0 bg-white shadow-lg transition-all duration-300 overflow-hidden ${
          isMobileMenuOpen ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <nav className="flex flex-col p-4 gap-2">
          <button
            onClick={() => scrollToSection('services')}
            className="text-left px-4 py-3 text-slate-600 hover:bg-slate-50 hover:text-teal-600 rounded-lg transition-colors"
          >
            {t.nav.services}
          </button>
          <button
            onClick={() => scrollToSection('about')}
            className="text-left px-4 py-3 text-slate-600 hover:bg-slate-50 hover:text-teal-600 rounded-lg transition-colors"
          >
            {t.nav.about}
          </button>
          <button
            onClick={() => scrollToSection('contact')}
            className="text-left px-4 py-3 text-slate-600 hover:bg-slate-50 hover:text-teal-600 rounded-lg transition-colors"
          >
            {t.nav.contact}
          </button>
          <Button
            onClick={() => scrollToSection('contact')}
            className="mt-2 bg-teal-600 hover:bg-teal-700 text-white rounded-full"
          >
            {t.nav.getQuote}
          </Button>
        </nav>
      </div>
    </header>
  );
};

export default Header;
